import { H2, Body1, colorsLight, H1, useParsedQueryString, Card } from "@coherehealth/common";
import { Queue, useSearchElasticStats } from "@coherehealth/qm-api";
import { Grid } from "@mui/material";
import { useState, useMemo, useEffect, ReactNode } from "react";
import { usePageResponsiveGridItemSize } from "../util/ChartUtil";
import { dueDateStatsQueueBasedQueryGenerator, filtersWithoutFaxAutoAttachFilter } from "../util/QueryUtil";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import config from "api/config";
import routes from "routes";
import { generatePath, useNavigate } from "react-router-dom";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    icon: {
      color: theme.palette.primary.main,
      width: "37px !important",
      height: "37px !important",
      marginRight: 13,
    },
    statsCard: {
      display: "flex",
      justifyContent: "center",
      height: 105,
      padding: "24px 16px 24px 13px",
      flex: "1 1 0px",
    },
    queueBasedStatsCard: {
      display: "flex",
      height: 105,
      padding: "24px 16px 24px 24px",
      "&:hover": {
        cursor: "pointer",
      },
    },
  })
);

interface StatsBarContentProps {
  queues: Queue[] | null;
}

const StatsBarContent = ({ queues }: StatsBarContentProps) => {
  const responsiveGridItemSize = usePageResponsiveGridItemSize();

  return (
    <div style={{ marginBottom: 40 }}>
      <H2 style={{ padding: "24px 0px" }}>Current queue volume</H2>

      <Grid container rowSpacing={2.5} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        {queues?.map((queue) => (
          <Grid item xs={responsiveGridItemSize} key={queue.id}>
            <QueueBasedStatsCard queue={queue} />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

interface StatsCardProps {
  title: string;
  data: number;
  icon: ReactNode | null;
  clickNavigation?: () => void;
}

const StatsCard = ({ title, data, icon, clickNavigation }: StatsCardProps) => {
  const classes = useStyles();

  return (
    <Card className={!!icon ? classes.statsCard : classes.queueBasedStatsCard} onClick={() => clickNavigation?.()}>
      {!!icon && <div style={{ display: "flex", alignItems: "center" }}>{icon}</div>}
      <div style={{ width: "100%" }}>
        <Body1
          style={{
            color: colorsLight.font.secondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </Body1>
        <H1>{data}</H1>
      </div>
    </Card>
  );
};

interface QueueBasedStatsCardProps {
  queue: Queue;
}

const QueueBasedStatsCard = ({ queue }: QueueBasedStatsCardProps) => {
  const navigate = useNavigate();
  const qs = useParsedQueryString();
  const [data, setData] = useState<number>();
  const searchPayload = useMemo(
    () => dueDateStatsQueueBasedQueryGenerator(filtersWithoutFaxAutoAttachFilter(queue.filters || [])),
    [queue.filters]
  );

  const { mutate: searchElasticStats } = useSearchElasticStats({
    base: `${config.QM_SERVICE_API_URL}`,
  });

  useEffect(() => {
    const fetchStatsData = async () => {
      const res = await searchElasticStats(searchPayload);
      setData(res.hits.totalHits?.value || 0);
    };

    fetchStatsData();
  }, [searchElasticStats, searchPayload]);

  const navigateToQueueVolumePage = (queue: Queue) => {
    navigate({
      pathname: generatePath(routes.REPORTS.QUEUE_VOLUME, { queueId: queue?.id }),
      search: `days=${qs.days ? qs.days : "14"}&skill=${qs.skill ? qs.skill : "All"}`,
    });
  };

  return (
    <StatsCard
      title={queue.displayConfig?.title || queue.name}
      data={data || 0}
      icon={null}
      clickNavigation={() => navigateToQueueVolumePage(queue)}
    />
  );
};

export default StatsBarContent;
